import React, { useContext } from 'react';
import Icon from '@ui/Icon';
import {
  Actions,
  Subjects,
  useIsManagedUser,
  useUserAbilitiesContext,
} from 'context/user/UserContext';
import { usePromptsContext } from 'context/prompts/PromptsContext';
import { useFeatureFlags } from 'components/FeatureFlags';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  LayoutElementState,
  useLayoutContext,
} from '../../BaseLayout/context/LayoutContext';
import {
  hideCommentsSidebar,
  showCommentsSidebar,
} from '../../../store/CommentsSlice';
import { StudioClipContext } from '../StudioClip';
import CommentIndicatorButton from '../../Comments/components/CommentIndicatorButton';
import StudioClipAction, { StudioClipActionProps } from './StudioClipAction';

interface StudioClipActionCommentProps
  extends Omit<StudioClipActionProps, 'onClick'> {}

const StudioClipActionComment = (props: StudioClipActionCommentProps) => {
  const { ...rest } = props;
  const { clip, breakpoint } = useContext(StudioClipContext);
  const commentsState = useAppSelector(appState => appState.comments);
  const appDispatch = useAppDispatch();
  const [, dispatch] = usePromptsContext();
  const isManagedUser = useIsManagedUser();
  const userAbility = useUserAbilitiesContext();
  const { setRightRailVisibility, rightRailVisibility } = useLayoutContext();
  const canUseComments =
    userAbility.can(Actions.UseFeature, Subjects.Comments) ||
    userAbility.can(Actions.Read, Subjects.Comments);
  const { isFeatureEnabled } = useFeatureFlags();
  const handleCommentButtonClick = React.useCallback(() => {
    if (!canUseComments) {
      dispatch({
        type: 'OpenCommentsUpsellPrompt',
        payload: {
          isManagedUser,
          onClick: () => {
            dispatch({
              type: 'OpenSubscriptionTiersPrompt',
              payload: {
                upgradeContext: 'default',
                upgradePath: 'Dashboard > Project > Comment',
              },
            });
          },
        },
      });
      return;
    }
    if (rightRailVisibility !== LayoutElementState.OPEN) {
      setRightRailVisibility(LayoutElementState.OPEN);
    }

    /**
     * Toggle behavior, close if clicking on same button
     */
    if (commentsState.showSidebar && commentsState.clipId === clip.id) {
      appDispatch(hideCommentsSidebar());
      return;
    }

    appDispatch(showCommentsSidebar({ clipId: clip.id, clipName: clip.name }));
  }, [
    appDispatch,
    canUseComments,
    clip.id,
    clip.name,
    commentsState.clipId,
    commentsState.showSidebar,
    dispatch,
    isFeatureEnabled,
    isManagedUser,
    rightRailVisibility,
    setRightRailVisibility,
  ]);

  if (breakpoint === 'xs') {
    return (
      <StudioClipAction.Mobile
        {...rest}
        menuItemText="Open comments"
        aria-label={`Open comments for clip ${clip.name}`}
        onClick={handleCommentButtonClick}
      >
        <Icon type="messageSquare02" />
      </StudioClipAction.Mobile>
    );
  }

  return (
    <CommentIndicatorButton
      aria-label={`Open comments for clip ${clip.name}`}
      commentsTotal={clip.commentCount || 0}
      unreadCommentsTotal={clip.unviewedCommentCount || 0}
      open={commentsState.showSidebar && commentsState.clipId === clip.id}
      onClick={handleCommentButtonClick}
    />
  );
};

export default StudioClipActionComment;
